import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const DailyCrewTools = () => (
  <Page
    title="Daily Crew Tools"
    intro="The checks and records that make up a crew day: the start-of-shift
    check, vehicle daily inspections, cleans, fuel, defects, your medicine
    bags and drawn-up medicines, and where to report an incident or raise a
    concern."
  >
    <Section title="Quick ways in">
      <p>
        The Home screen's <strong>Quick Links</strong> card holds the daily
        tools — Start of Shift Check, Vehicle Clean, Record Fuel, Report a
        Defect, Report an Incident, Speak Up, Meds Management, and Patient
        Care Record (plus My PTS Duty or Make Ready where they apply to you).
        The <strong>+</strong> Quick Add menu in the header and the ⌘K search
        offer the same create actions from any page.
      </p>
    </Section>

    <Section title="Start of Shift Check">
      <p>
        <DocLink text="Start of Shift Check" url="/start-of-shift" /> is one
        form for the whole beginning of your shift. Pick your vehicle, record
        the fuel level and mileage, then work through:
      </p>
      <Bullets>
        <Bullet>
          <strong>The vehicle check (VDI)</strong> — the daily inspection
          checklist for that vehicle type, plus whether the vehicle is
          useable.
        </Bullet>
        <Bullet>
          <strong>Equipment &amp; consumables</strong> — the vehicle's
          inventory, editable as you check it.
        </Bullet>
        <Bullet>
          <strong>Drug bags</strong> — confirm each bag's seal. If the seal
          has a QR tag you can scan it to prove you checked the right bag,
          and a bag found without the seal it should have is reported to the
          medicines governance trail automatically. Bags you have with you
          can be assigned to the vehicle as part of the check.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Vehicle Daily Inspections (VDIs)">
      <p>
        A VDI can also be submitted on its own from{' '}
        <DocLink text="VDI" url="/vdi" />. Anything you mark as a fault needs
        a comment, and the form asks whether the faults need a defect report
        and whether the vehicle is still useable. Completed VDIs are listed
        under <DocLink text="VDIs" url="/vdis" />, where you can open the
        full report of any check.
      </p>
    </Section>

    <Section title="Recording a vehicle clean">
      <p>
        <DocLink text="Vehicle Clean" url="/vehicle-clean" /> records an{' '}
        <strong>Interim</strong>, <strong>Deep (Scheduled)</strong>, or{' '}
        <strong>Decontamination</strong> clean: the vehicle, start and end
        times, who cleaned it, and the cleaning checklist for that vehicle
        and clean type. Deep cleans and decontaminations also ask whether an
        ATP test was completed, a decontamination needs its reason, and the
        form shows when the vehicle's next deep clean is due.
      </p>
    </Section>

    <Section title="Recording fuel">
      <p>
        <DocLink text="Record Fuel" url="/record-fuel" /> logs a refuel: the
        vehicle, when you refuelled, the litres added, the price per litre
        (the total is worked out for you), the current mileage, and a photo
        of the receipt.
      </p>
    </Section>

    <Section title="Reporting a defect">
      <p>
        <DocLink text="Report a Defect" url="/report-defect" /> is open to
        everyone, so problems get raised the moment they're spotted. Say
        whether it affects a vehicle (pick it from the list) or an item of
        equipment (type the asset tag or scan its QR code), describe the
        problem, flag it as an infection prevention and control issue if
        relevant, and attach photos. For a vehicle you're asked whether it is
        still fit for use — a vehicle reported as not fit for use is normally
        taken out of service automatically.
      </p>
      <Info title="What happens after you report">
        Your organisation's designated defect contacts (and, by default, the
        Fleet and Make Ready teams) are alerted straight away, and the defect
        is tracked through to completion. If your report closes as{' '}
        <strong>Duplicate - Closed</strong>, that just means the same defect
        was already logged — it still counts as dealt with.
      </Info>
    </Section>

    <Section title="Your medicine bags and drawn-up medicines">
      <p>
        <DocLink text="Check Meds Bag" url="/checkmedsbag" /> lists the
        medicine bags you can work with — select a bag to view its contents,
        assign it to yourself or a vehicle, return it, or record a check.
      </p>
      <p>
        <DocLink text="My Drawn-up Medicines" url="/drawn-up-medicines" />{' '}
        lists every medicine you currently have drawn up, with a live expiry
        countdown on each, until it is given, handed over, or discarded with
        a reason. For the wider medicines workflow, see{' '}
        <DocLink
          docsPage
          url="/clinical-governance/medicines-management"
          text="Medicines Management"
        />
        .
      </p>
    </Section>

    <Section title="Incidents and concerns">
      <p>
        <DocLink text="Report an Incident" url="/report-incident" /> walks
        you through a step-by-step form — details, description, people,
        vehicles, equipment, medicines, and attachments. All staff can report
        incidents; report as soon as you can, ideally within 24 hours, while
        details are fresh.
      </p>
      <p>
        <DocLink text="Speak Up" url="/speak-up" /> is for concerns rather
        than incidents, and is anonymous by default — the server never stores
        who submitted an anonymous concern. See{' '}
        <DocLink
          docsPage
          url="/clinical-governance/freedom-to-speak-up"
          text="Freedom to Speak Up"
        />
        .
      </p>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Every tool on this page is open to any signed-in staff member — none
        of them needs a management team. Some depend on your organisation's
        setup — for example, Speak Up only accepts anonymous concerns where
        your organisation has switched anonymous reporting on.
      </Info>
    </Section>
  </Page>
)

export default DailyCrewTools
